import axios from 'axios';
import oktonBankApi from './oktonBankApi';
import { resolverRota } from './oktonBankRoutes';

interface RespostaErro {
  success?: boolean;
  msg?: string;
}

// O Backend do OktonBank responde 200 com { success: false, msg } em erro de
// negócio, no mesmo formato que o IFLEX já usava - e também pode devolver o
// erro via status HTTP (403/409/422/500), sempre no mesmo formato. Normalizar
// aqui evita que cada hook precise checar as duas formas.
function garantirSucesso<T extends RespostaErro>(dados: T): T {
  if (!dados || typeof dados !== 'object') {
    throw new Error(
      'O Bancário recebeu uma resposta inválida. Tente novamente.',
    );
  }
  if (dados.success === false) {
    throw new Error(dados.msg || 'Não foi possível concluir a operação.');
  }
  return dados;
}

function erroDoBackend(erro: unknown): never {
  if (axios.isAxiosError(erro)) {
    const dados = erro.response?.data as RespostaErro | undefined;
    if (dados?.msg) {
      throw new Error(dados.msg);
    }
  }
  throw erro;
}

// action/controller do IFLEX viram recurso/action daqui: resolverRota() acha
// o endpoint do Laravel equivalente (ver oktonBankRoutes.ts). GET manda os
// parâmetros como querystring; os demais métodos, como corpo JSON.
async function executar<T>(
  recurso: string,
  action: string,
  params: Record<string, unknown>,
): Promise<T> {
  const { metodo, url } = resolverRota(recurso, action, params);
  try {
    let response;
    if (metodo === 'get') {
      response = await oktonBankApi.get<T>(url, { params });
    } else if (metodo === 'delete') {
      response = await oktonBankApi.delete<T>(url);
    } else if (metodo === 'post') {
      response = await oktonBankApi.post<T>(url, params);
    } else {
      response = await oktonBankApi.put<T>(url, params);
    }
    return garantirSucesso(response.data as T & RespostaErro);
  } catch (erro) {
    return erroDoBackend(erro);
  }
}

export async function chamarOktonBank<T>(
  recurso: string,
  action: string,
  params: Record<string, string | number> = {},
): Promise<T> {
  return executar<T>(recurso, action, params);
}

// O corpo vai como JSON: aceita objeto aninhado (ex.: o endereço do Cliente),
// ao contrário do GET de chamarOktonBank, que vira querystring.
export async function enviarOktonBank<T>(
  recurso: string,
  action: string,
  params: Record<string, unknown> = {},
): Promise<T> {
  return executar<T>(recurso, action, params);
}
